import { useTranslation } from 'react-i18next';
import {
  BAYTEK_NAME,
  BEHR_NAME,
  CAPITAL_WRAPS_NAME,
  COAST_NAME,
  EVERCONNECT_NAME,
  GUSTO_NAME,
  HD_SUPPLY_NAME,
  HDX_NAME,
  NOVO_NAME,
  LENNOX_COMMERCIAL_NAME,
  LENNOX_RESIDENTIAL_NAME,
  POSTCARDMANIA_NAME,
  RUUD_NAME,
  RINNAI_NAME,
  SPARTAN_TOOL_NAME,
  SUNBELT_RENTALS_NAME,
  WE_SUPPLY_TRADES_ACCOUNT_URL,
  WESUPPLYTRADES_NAME,
  WINSUPPLY_NAME,
  THE_HOME_DEPOT_NAME,
  BAYTEK_CODE,
  BEHR_CODE,
  CAPITAL_WRAPS_CODE,
  COAST_CODE,
  EVERCONNECT_CODE,
  GOODMAN_DAIKIN_AMANA_CODE,
  GUSTO_CODE,
  HD_SUPPLY_CODE,
  HDX_CODE,
  NOVO_CODE,
  LENNOX_COMMERCIAL_CODE,
  LENNOX_RESIDENTIAL_CODE,
  POSTCARDMANIA_CODE,
  RHEEM_CODE,
  RUUD_CODE,
  RINNAI_CODE,
  SPARTAN_TOOL_CODE,
  SUNBELT_RENTALS_CODE,
  WESUPPLYTRADES_CODE,
  WINSUPPLY_CODE,
  THE_HOME_DEPOT_CODE,
  PARTNER_TIER_CODE_SUFFIXES,
} from '../../../../common/constants';
import { useAppSelector } from '../../../../store/hooks';
import { selectMemberInformation } from '../../../../store/slices/onboardingSlice';

export type PartnerSpecificQuestionType = 'radio' | 'select' | 'text' | 'multi-select' | 'checkbox';

/**
 * Some partners have one row per membership tier (edge / association / srt) -
 * each with its own code because of a UNIQUE(code) constraint on the partners
 * table. A non-edge code is the edge code plus that tier's suffix, one of
 * PARTNER_TIER_CODE_SUFFIXES. These maps are keyed by the edge code; this helper
 * also registers each config under every suffixed code so lookups work for all
 * tiers (an Association member's partner.code is the `-AM` variant, a
 * Roundtable member's the `-SRT` one). Aliases for partners that only have one
 * row are harmless.
 * TODO: drop once partners are consolidated to one row and config moves to the
 * DB (future ticket).
 */
const withSrtCodeAliases = <T>(map: { [code: string]: T }): { [code: string]: T } => {
  const withAliases = { ...map };
  Object.keys(map).forEach((code) => {
    PARTNER_TIER_CODE_SUFFIXES.forEach((suffix) => {
      withAliases[`${code}${suffix}`] = map[code];
    });
  });
  return withAliases;
};

export const CompanyVehicleQuestionOptions = ['1-2', '3-4', '5-9', '10+'];

export interface PartnerSpecificQuestion {
  key: string;
  title: string; // question alias used as column names in the report
  label?: string | null;
  subLabel?: string | null;
  type: PartnerSpecificQuestionType;
  placeholder?: string | null; // used for 'select' | 'text' | 'multi-select' input types
  required?: boolean;
  options?: string[];
  validation?: {
    pattern?: {
      value: RegExp;
      message: string;
    };
    minLength?: {
      value: number;
      message: string;
    };
  };
  defaultValue?: string | string[];
  fullWidth?: boolean;
}

export type PartnerSpecificFormInputValueType = string | string[]

export interface PartnerSpecificFormData {
  version: string;
  questions: Record<string, {
    title: string;
    answer: PartnerSpecificFormInputValueType;
  }>
}

export interface PartnerSpecificFormConfig {
  partnerName: string;
  version: string;
  questions: PartnerSpecificQuestion[];
  existingAccountExtraQuestion?: PartnerSpecificQuestion[];
  hasExtraEnrollmentSteps?: boolean;
  redirectPath?: string;
  submitCTAText?: string | null;
  instructions?: string | null;
  completeEnrollmentOnConfirmationClick?: boolean;
}

export const usePartnerSpecificFormConfigMap = () => {
  const { t } = useTranslation();
  const memberInformation = useAppSelector(selectMemberInformation);
  const partnerSpecificFormConfigMap:
  { [key: string]: PartnerSpecificFormConfig } = {
    // In the question key:
    // - `title` refers to the value sent to the backend
    // - `label` refers to the text displayed to the user
    [HD_SUPPLY_CODE]: {
      partnerName: HD_SUPPLY_NAME,
      version: 'hdSupply-questions-v1',
      completeEnrollmentOnConfirmationClick: true,
      questions: [
        {
          key: 'hd_supply_familiar',
          title: t('hdSupply.partnerSpecificFormConfig.hd_supply_familiar_title'),
          label: t('hdSupply.partnerSpecificFormConfig.hd_supply_familiar_label'),
          type: 'radio',
          required: true,
          options: ['Yes', 'No'],
        },
        {
          key: 'number_of_vehicles',
          title: t('hdSupply.partnerSpecificFormConfig.hd_supply_vehicles_title'),
          label: t('hdSupply.partnerSpecificFormConfig.hd_supply_vehicles_label'),
          type: 'select',
          required: true,
          options: CompanyVehicleQuestionOptions,
          defaultValue: memberInformation?.company
            ?.partnerEnrollmentQuestions?.questions?.number_of_vehicles?.answer,
        },
      ],
    },
    [CAPITAL_WRAPS_CODE]: {
      partnerName: CAPITAL_WRAPS_NAME,
      version: 'capitalWraps-questions-v1',
      questions: [
        {
          key: 'number_of_vehicles',
          title: t('capitalWraps.partnerSpecificFormConfig.capital_wraps_vehicles_title'),
          label: t('capitalWraps.partnerSpecificFormConfig.capital_wraps_vehicles_label'),
          placeholder: t('verification.defaultOptionsPlaceholder'),
          type: 'select',
          required: true,
          options: CompanyVehicleQuestionOptions,
          defaultValue: memberInformation?.company
            ?.partnerEnrollmentQuestions?.questions?.number_of_vehicles?.answer,
        },
        {
          key: 'capital_wraps_contact',
          title: t('capitalWraps.partnerSpecificFormConfig.capital_wraps_contact_title'),
          label: t('capitalWraps.partnerSpecificFormConfig.capital_wraps_contact_label'),
          placeholder: t('verification.defaultOptionsPlaceholder'),
          type: 'select',
          required: true,
          options: ['Email', 'Text', 'Call'],
        },
        {
          key: 'capital_wraps_purchase',
          title: t('capitalWraps.partnerSpecificFormConfig.capital_wraps_purchase_title'),
          label: t('capitalWraps.partnerSpecificFormConfig.capital_wraps_purchase_label'),
          placeholder: t('verification.defaultOptionsPlaceholder'),
          type: 'select',
          required: true,
          options: ['< 1 month', '1-3 months', '3+ months', 'Just browsing for now'],
        },
      ],
    },
    [SPARTAN_TOOL_CODE]: {
      partnerName: SPARTAN_TOOL_NAME,
      version: 'spartanTool-questions-v1',
      completeEnrollmentOnConfirmationClick: true,
      questions: [
        {
          key: 'spartan_tool_familiar',
          title: t('spartanTool.partnerSpecificFormConfig.spartan_tool_familiar_title'),
          label: t('spartanTool.partnerSpecificFormConfig.spartan_tool_familiar_label'),
          type: 'radio',
          required: true,
          options: ['Yes', 'No'],
        },
        {
          key: 'spartan_tool_start_shopping',
          placeholder: t('verification.defaultMultiSelectPlaceholder'),
          title: t('spartanTool.partnerSpecificFormConfig.spartan_tool_start_shopping_title'),
          label: t('spartanTool.partnerSpecificFormConfig.spartan_tool_start_shopping_label'),
          type: 'multi-select',
          required: true,
          options: [
            t('spartanTool.partnerSpecificFormConfig.spartan_tool_start_shopping_options1'),
            t('spartanTool.partnerSpecificFormConfig.spartan_tool_start_shopping_options2'),
            t('spartanTool.partnerSpecificFormConfig.spartan_tool_start_shopping_options3'),
          ],
        },
      ],
    },
    [WESUPPLYTRADES_CODE]: {
      partnerName: WESUPPLYTRADES_NAME,
      version: 'weSupplyTrades-questions-v1',
      completeEnrollmentOnConfirmationClick: true,
      questions: [
        {
          key: 'wesupplytrades_familiar',
          title: t('weSupplyTrades.partnerSpecificFormConfig.wesupplytrades_familiar_title'),
          label: t('weSupplyTrades.partnerSpecificFormConfig.wesupplytrades_familiar_label'),
          type: 'radio',
          required: true,
          options: ['Yes', 'No'],
        },
        {
          key: 'wesupplytrades_first_purchase',
          title: t('weSupplyTrades.partnerSpecificFormConfig.wesupplytrades_first_purchase_title'),
          label: t('weSupplyTrades.partnerSpecificFormConfig.wesupplytrades_first_purchase_label'),
          type: 'select',
          required: true,
          options: ['Week', 'Month', 'Quarter', 'Year'],
        },
      ],
    },
    [WINSUPPLY_CODE]: {
      partnerName: WINSUPPLY_NAME,
      version: 'winSupply-questions-v1',
      completeEnrollmentOnConfirmationClick: true,
      questions: [
        {
          key: 'winsupply_familiar',
          title: t('winsupply.partnerSpecificFormConfig.winsupply_familiar_title'),
          label: t('winsupply.partnerSpecificFormConfig.winsupply_familiar_label'),
          type: 'radio',
          required: true,
          options: ['Yes', 'No'],
        },
        {
          key: 'winsupply_first_purchase',
          title: t('winsupply.partnerSpecificFormConfig.winsupply_first_purchase_title'),
          label: t('winsupply.partnerSpecificFormConfig.winsupply_first_purchase_label'),
          placeholder: t('verification.defaultOptionsPlaceholder'),
          type: 'select',
          required: true,
          options: ['Week', 'Month', 'Quarter', 'Year'],
        },
      ],
    },
    [COAST_CODE]: {
      partnerName: COAST_NAME,
      version: 'coast-questions-v1',
      completeEnrollmentOnConfirmationClick: true,
      questions: [
        {
          key: 'number_of_vehicles',
          title: t('coast.partnerSpecificFormConfig.coast_vehicles_title'),
          label: t('coast.partnerSpecificFormConfig.coast_vehicles_label'),
          placeholder: t('verification.defaultOptionsPlaceholder'),
          type: 'select',
          required: true,
          options: CompanyVehicleQuestionOptions,
          defaultValue: memberInformation?.company
            ?.partnerEnrollmentQuestions?.questions?.number_of_vehicles?.answer,
        },
        {
          key: 'coast_enrollment_preference',
          title: t('coast.partnerSpecificFormConfig.coast_enrollment_preference_title'),
          label: t('coast.partnerSpecificFormConfig.coast_enrollment_preference_label'),
          placeholder: t('verification.defaultOptionsPlaceholder'),
          type: 'select',
          required: true,
          options: ['Speak to an acct manager', 'Enroll online'],
        },
      ],
    },
    [SUNBELT_RENTALS_CODE]: {
      partnerName: SUNBELT_RENTALS_NAME,
      version: 'sunbeltRentals-questions-v1',
      questions: [
        {
          key: 'sunbelt_rentals_participation',
          title: t('sunbeltRentals.partnerSpecificFormConfig.sunbelt_rentals_participation_title'),
          label: t('sunbeltRentals.partnerSpecificFormConfig.extra_existing_account_question_label1'),
          subLabel: t('sunbeltRentals.partnerSpecificFormConfig.extra_existing_account_question_label2'),
          type: 'checkbox',
          required: true,
        },
      ],
      existingAccountExtraQuestion: [
        {
          key: 'sunbelt_rentals_participation',
          title: t('sunbeltRentals.partnerSpecificFormConfig.sunbelt_rentals_participation_title'),
          label: t('sunbeltRentals.partnerSpecificFormConfig.sunbelt_rentals_participation_label'),
          type: 'checkbox',
          required: true,
        },
      ],
      hasExtraEnrollmentSteps: true,
      redirectPath: 'https://www.sunbeltrentals.com/createaccount/customer/?c=1&o=msp&s=25217',
      submitCTAText: t('sunbeltRentals.partnerSpecificFormConfig.submitCTAText'),
      instructions: t('sunbeltRentals.partnerSpecificFormConfig.instructionsDesc'),
    },
    [EVERCONNECT_CODE]: {
      partnerName: EVERCONNECT_NAME,
      version: 'everConnect-questions-v1',
      questions: [
        {
          key: 'everconnect_services_interest',
          title: t('everConnect.partnerSpecificFormConfig.everconnect_services_interest_title'),
          label: t('everConnect.partnerSpecificFormConfig.everconnect_services_interest_label'),
          type: 'radio',
          required: true,
          fullWidth: true,
          options: [
            'Exclusive Lead Generation',
            'Digital Marketing (websites, SEO, PPC, social media)',
            'Both',
          ],
        },
      ],
    },
  };

  return withSrtCodeAliases(partnerSpecificFormConfigMap);
};

export interface PartnerExternalEnrollmentFormConfig {
  partnerName: string;
  externalEnrollmentForm?: string;
  ssoDestination?: string;
}

export const usePartnerExternalEnrollmentForms = () => {
  const partnerExternalEnrollmentForms: { [key: string]: PartnerExternalEnrollmentFormConfig } = {
    [GOODMAN_DAIKIN_AMANA_CODE]: {
      partnerName: 'Goodman, Amana & Daikin HVAC Equipment',
      externalEnrollmentForm: 'https://servicenation.formstack.com/forms/goodman_rebate_form',
    },
    [RHEEM_CODE]: {
      partnerName: 'Rheem',
      externalEnrollmentForm: 'https://servicenation.formstack.com/forms/rheem_ruud_rebate_form',
    },
    [RUUD_CODE]: {
      partnerName: RUUD_NAME,
      externalEnrollmentForm: 'https://servicenation.formstack.com/forms/rheem_ruud_rebate_form',
    },
    [LENNOX_RESIDENTIAL_CODE]: {
      partnerName: LENNOX_RESIDENTIAL_NAME,
      externalEnrollmentForm: 'https://servicenation.formstack.com/forms/lennox_rebate_form',
    },
    [LENNOX_COMMERCIAL_CODE]: {
      partnerName: LENNOX_COMMERCIAL_NAME,
      externalEnrollmentForm: 'https://servicenation.formstack.com/forms/lennox_commercial_rebate_form',
    },
    [THE_HOME_DEPOT_CODE]: {
      partnerName: THE_HOME_DEPOT_NAME,
      externalEnrollmentForm: 'https://app.servicenation.com/pdfs/service-nation-home-depot-pro-instructions.pdf',
    },
  };

  return partnerExternalEnrollmentForms;
};

export interface PartnerSpecificModalConfig {
  partnerName: string;
  confirmationBtnLink: string;
  confirmationBtnText: string;
  confirmationDescription: string;
  confirmationDescriptionExistingAcc?: string;
  confirmationBtnTextExistingAcc?: string;
  confirmationBtnLinkExistingAcc?: string;
  manageRewardsBtnLink: string;
  manageRewardsBtnText: string;
  manageRewardsDescription: string;
  manageRewardsDescriptionKey?: string;
  manageRewardsDescriptionLink?: string;
  extraStepConfirmationBtnText?: string;
  extraStepConfirmationDescription?: string;
  submitDescription?: string;
  isManageRewardExternalLink?: boolean;
}

export const usePartnerSpecificModalConfigMap = () => {
  const { t } = useTranslation();
  const partnerSpecificModalConfigMap:
  { [key: string]: PartnerSpecificModalConfig } = {
    [HD_SUPPLY_CODE]: {
      partnerName: HD_SUPPLY_NAME,
      confirmationBtnLink: 'https://www.ebarnett.com/registration/becomeacustomer',
      confirmationBtnText: t('hdSupply.signUpDialog.confirmationBtn'),
      confirmationDescription: t('hdSupply.signUpDialog.confirmationDescription'),
      confirmationDescriptionExistingAcc: t('hdSupply.signUpDialog.confirmationDescriptionExistingAcc') as string,
      confirmationBtnTextExistingAcc: t('hdSupply.signUpDialog.confirmationBtnExistingAcc') as string,
      confirmationBtnLinkExistingAcc: 'https://www.ebarnett.com/Security/Login?returnUrl=/Account',
      manageRewardsDescription: t('hdSupply.signUpDialog.manageRewardsDescription'),
      manageRewardsBtnLink: 'https://www.ebarnett.com/Security/Login?returnUrl=/Account',
      manageRewardsBtnText: t('hdSupply.signUpDialog.manageRewardsBtn'),
    },
    [CAPITAL_WRAPS_CODE]: {
      partnerName: CAPITAL_WRAPS_NAME,
      confirmationBtnLink: 'https://www.capitalwraps.com/gallery/',
      confirmationBtnText: t('capitalWraps.signUpDialog.confirmationBtn'),
      confirmationDescription: t('capitalWraps.signUpDialog.confirmationDescription'),
      manageRewardsDescription: t('capitalWraps.signUpDialog.manageRewardsDescription'),
      manageRewardsBtnLink: 'https://www.capitalwraps.com/',
      manageRewardsBtnText: t('capitalWraps.signUpDialog.manageRewardsBtn'),
    },
    [SPARTAN_TOOL_CODE]: {
      partnerName: SPARTAN_TOOL_NAME,
      confirmationBtnLink: 'https://spartantool.com/',
      confirmationBtnText: t('spartanTool.signUpDialog.confirmationBtn'),
      confirmationDescription: t('spartanTool.signUpDialog.confirmationDescription'),
      manageRewardsDescription: t('spartanTool.signUpDialog.manageRewardsDescription'),
      manageRewardsBtnLink: 'https://spartantool.com/',
      manageRewardsBtnText: t('spartanTool.signUpDialog.manageRewardsBtn'),
    },
    [WESUPPLYTRADES_CODE]: {
      partnerName: WESUPPLYTRADES_NAME,
      confirmationBtnLink: WE_SUPPLY_TRADES_ACCOUNT_URL,
      confirmationBtnText: t('weSupplyTrades.signUpDialog.confirmationBtn'),
      confirmationDescription: t('weSupplyTrades.signUpDialog.confirmationDescription'),
      manageRewardsDescription: t('weSupplyTrades.signUpDialog.manageRewardsDescription'),
      manageRewardsBtnLink: 'https://www.wesupplytrades.com/',
      manageRewardsBtnText: t('weSupplyTrades.signUpDialog.manageRewardsBtn'),
    },
    [WINSUPPLY_CODE]: {
      partnerName: WINSUPPLY_NAME,
      confirmationBtnLink: 'https://www.winsupplyinc.com/ftue',
      confirmationBtnText: t('winsupply.signUpDialog.confirmationBtn'),
      confirmationDescription: t('winsupply.signUpDialog.confirmationDescription'),
      confirmationDescriptionExistingAcc: t('winsupply.signUpDialog.confirmationDescriptionExistingAcc') as string,
      confirmationBtnTextExistingAcc: t('winsupply.signUpDialog.confirmationBtnExistingAcc') as string,
      confirmationBtnLinkExistingAcc: 'https://www.winsupplyinc.com/ftue',
      manageRewardsDescription: t('winsupply.signUpDialog.manageRewardsDescription'),
      manageRewardsBtnLink: 'https://www.winsupplyinc.com/account/login',
      manageRewardsBtnText: t('winsupply.signUpDialog.manageRewardsBtn'),
    },
    [COAST_CODE]: {
      partnerName: COAST_NAME,
      confirmationBtnLink: 'https://coastpay.com/lp/everpro/',
      confirmationBtnText: t('coast.signUpDialog.confirmationBtn'),
      confirmationDescription: t('coast.signUpDialog.confirmationDescription'),
      manageRewardsDescription: t('coast.signUpDialog.manageRewardsDescription'),
      manageRewardsDescriptionKey: 'coast.signUpDialog.manageRewardsDescription',
      manageRewardsDescriptionLink: 'https://coastpay.com/lp/everpro/',
      manageRewardsBtnLink: 'https://portal.coastpay.com/auth?first_utm_save_time=1749041168799',
      manageRewardsBtnText: t('coast.signUpDialog.manageRewardsBtn'),
    },
    [SUNBELT_RENTALS_CODE]: {
      partnerName: SUNBELT_RENTALS_NAME,
      confirmationBtnLink: 'https://www.sunbeltrentals.com/createaccount/customer/?c=1&o=msp&s=25217',
      confirmationBtnText: t('sunbeltRentals.signUpDialog.confirmationBtn'),
      confirmationDescription: t('sunbeltRentals.signUpDialog.confirmationDescription'),
      confirmationDescriptionExistingAcc: t('sunbeltRentals.signUpDialog.confirmationDescriptionExistingAcc') as string,
      confirmationBtnTextExistingAcc: t('sunbeltRentals.signUpDialog.confirmationBtnExistingAcc') as string,
      confirmationBtnLinkExistingAcc: 'https://www.sunbeltrentals.com/',
      manageRewardsDescription: t('sunbeltRentals.signUpDialog.manageRewardsDescription'),
      manageRewardsDescriptionKey: 'sunbeltRentals.signUpDialog.manageRewardsDescription',
      manageRewardsDescriptionLink: 'https://www.sunbeltrentals.com/createaccount/customer/?c=1&o=msp&s=25217',
      manageRewardsBtnLink: 'https://www.sunbeltrentals.com/location/',
      manageRewardsBtnText: t('sunbeltRentals.signUpDialog.manageRewardsBtn'),
      extraStepConfirmationBtnText: t('sunbeltRentals.signUpDialog.extraStepConfirmationBtnText') as string,
      extraStepConfirmationDescription: t('sunbeltRentals.signUpDialog.extraStepConfirmationDescription') as string,
    },
    [EVERCONNECT_CODE]: {
      partnerName: EVERCONNECT_NAME,
      confirmationBtnLink: 'https://www.everconnect.com/services/pay-per-call',
      confirmationBtnText: t('everConnect.signUpDialog.confirmationBtn'),
      confirmationDescription: t('everConnect.signUpDialog.confirmationDescription'),
      manageRewardsDescription: t('everConnect.signUpDialog.manageRewardsDescription'),
      manageRewardsDescriptionKey: 'everConnect.signUpDialog.manageRewardsDescription',
      manageRewardsBtnLink: 'https://www.everconnect.com/services/pay-per-call',
      manageRewardsBtnText: t('everConnect.signUpDialog.manageRewardsBtn'),
      submitDescription: t('everConnect.partnerSpecificFormConfig.modalDesc') as string,
    },
    [RINNAI_CODE]: {
      partnerName: RINNAI_NAME,
      confirmationBtnLink: 'https://d2o7bp9oz3gksv.cloudfront.net/Rinnai_Qualifying_Products_c385409785.pdf',
      confirmationBtnText: t('rinnai.signUpDialog.confirmationBtn'),
      confirmationDescription: t('rinnai.signUpDialog.confirmationDescription'),
      manageRewardsDescription: t('rinnai.signUpDialog.manageRewardsDescription'),
      manageRewardsBtnLink: 'https://d2o7bp9oz3gksv.cloudfront.net/Rinnai_Qualifying_Products_c385409785.pdf',
      manageRewardsBtnText: t('rinnai.signUpDialog.manageRewardsBtn'),
    },
    [NOVO_CODE]: {
      partnerName: NOVO_NAME,
      confirmationBtnLink: 'https://www.novowater.com/',
      confirmationBtnText: t('novoProfessionalWaterSystems.signUpDialog.confirmationBtn'),
      confirmationDescription: t('novoProfessionalWaterSystems.signUpDialog.confirmationDescription'),
      manageRewardsDescription: t('novoProfessionalWaterSystems.signUpDialog.manageRewardsDescription'),
      manageRewardsBtnLink: 'https://www.novowater.com/',
      manageRewardsBtnText: t('novoProfessionalWaterSystems.signUpDialog.manageRewardsBtn'),
    },
    [BEHR_CODE]: {
      partnerName: BEHR_NAME,
      confirmationBtnLink: 'http://behr.com',
      confirmationBtnText: t('behr.signUpDialog.confirmationBtn'),
      confirmationDescription: t('behr.signUpDialog.confirmationDescription'),
      manageRewardsDescription: t('behr.signUpDialog.manageRewardsDescription'),
      manageRewardsBtnLink: 'http://behr.com',
      manageRewardsBtnText: t('behr.signUpDialog.manageRewardsBtn'),
    },
    [HDX_CODE]: {
      partnerName: HDX_NAME,
      confirmationBtnLink: 'https://www.homedepot.com/s/hdx%20air%20filters?NCNI-5',
      confirmationBtnText: t('hdx.signUpDialog.confirmationBtn'),
      confirmationDescription: t('hdx.signUpDialog.confirmationDescription'),
      manageRewardsDescription: t('hdx.signUpDialog.manageRewardsDescription'),
      manageRewardsBtnLink: 'https://www.homedepot.com/s/hdx%20air%20filters?NCNI-5',
      manageRewardsBtnText: t('hdx.signUpDialog.manageRewardsBtn'),
    },
    [POSTCARDMANIA_CODE]: {
      partnerName: POSTCARDMANIA_NAME,
      confirmationBtnLink: 'https://www.postcardmania.com/',
      confirmationBtnText: t('postcardMania.signUpDialog.confirmationBtn'),
      confirmationDescription: t('postcardMania.signUpDialog.confirmationDescription'),
      manageRewardsDescription: t('postcardMania.signUpDialog.manageRewardsDescription'),
      manageRewardsDescriptionKey: 'postcardMania.signUpDialog.manageRewardsDescription',
      manageRewardsBtnLink: '/partners/postcardmania',
      isManageRewardExternalLink: true,
      manageRewardsBtnText: t('postcardMania.signUpDialog.manageRewardsBtn'),
      submitDescription: t('postcardMania.partnerSpecificFormConfig.modalDesc') as string,
    },
    [BAYTEK_CODE]: {
      partnerName: BAYTEK_NAME,
      confirmationBtnLink: 'https://baytek.com/',
      confirmationBtnText: t('baytek.signUpDialog.confirmationBtn'),
      confirmationDescription: t('baytek.signUpDialog.confirmationDescription'),
      manageRewardsDescription: t('baytek.signUpDialog.manageRewardsDescription'),
      manageRewardsDescriptionKey: 'baytek.signUpDialog.manageRewardsDescription',
      manageRewardsBtnLink: '/partners/baytek',
      manageRewardsBtnText: t('baytek.signUpDialog.manageRewardsBtn'),
      submitDescription: t('baytek.partnerSpecificFormConfig.modalDesc') as string,
    },
    [GUSTO_CODE]: {
      partnerName: GUSTO_NAME,
      confirmationBtnLink: 'https://gusto.com/go/bd/everpro',
      confirmationBtnText: t('gusto.signUpDialog.confirmationBtn'),
      confirmationDescription: t('gusto.signUpDialog.confirmationDescription'),
      manageRewardsDescription: t('gusto.signUpDialog.manageRewardsDescription'),
      manageRewardsDescriptionKey: 'gusto.signUpDialog.manageRewardsDescription',
      manageRewardsBtnLink: '/partners/gusto',
      manageRewardsBtnText: t('gusto.signUpDialog.manageRewardsBtn'),
      submitDescription: t('gusto.partnerSpecificFormConfig.modalDesc') as string,
    },
  };
  return withSrtCodeAliases(partnerSpecificModalConfigMap);
};
